import { z } from "zod";
import { EventParticipantStatusSchema } from "./participant-state.schemas.js";
import type { EventParticipantStatusType } from "./participant-state.schemas.js";
import { eventParticipantProfileSchema } from "./event-participant-profile.schemas.js";
import { EmailAddressSchema } from "./email.schemas.js";
import { E164PhoneNumberSchema } from "./phone.schemas.js";
import {
  PersonalLinkedInUrlSchema,
  LinkedInUrlSchema,
} from "./linkedin-url.schemas.js";
import { ParticipantPrimaryContactSchema } from "./participant-contact.schemas.js";
import {
  EventParticipantIdPublicSchema,
  EventIdPublicSchema,
  UserIdPublicSchema,
} from "./branded-id.schemas.js";
import { userDataItemSchema } from "./user-data-item.schemas.js";
import {
  statusHistoryEntrySchema,
  type StatusHistoryEntry,
} from "./status-history.schemas.js";
import { UrlStringSchema } from "./url-string.schemas.js";
import { participantAssumptionsSchema } from "./participant-assumptions.schemas.js";
import { LocalizedTextSchema } from "./localized-text.schemas.js";

const _eventParticipantCreatableSchema = z.object({
  primaryContact: ParticipantPrimaryContactSchema,

  secondaryEmails: z.array(EmailAddressSchema).readonly().nullable(),
  secondaryPhones: z.array(E164PhoneNumberSchema).readonly().nullable(),

  assistantEmails: z.array(EmailAddressSchema).readonly().nullable(),
  assistantPhones: z.array(E164PhoneNumberSchema).readonly().nullable(),

  firstName: userDataItemSchema(z.string().nullable()),
  lastName: userDataItemSchema(z.string().nullable()),
  fullName: userDataItemSchema(z.string().nullable()),
  nickName: userDataItemSchema(z.string().nullable()),

  personalLinkedInUrl: PersonalLinkedInUrlSchema.nullable(),
  relatedLinkedInUrls: z.array(LinkedInUrlSchema).readonly().nullable(),

  companyName: z.string().nullable(),
  companyUrl: z.string().nullable(),
  linkedInCompanyUrl: z.string().nullable(),

  vip: z.boolean(),

  title: userDataItemSchema(z.string()).nullable(),
  bio: userDataItemSchema(LocalizedTextSchema).nullable(),
  summary: userDataItemSchema(LocalizedTextSchema).nullable(),
  photoUrl: userDataItemSchema(UrlStringSchema).nullable(),
  assumptions: participantAssumptionsSchema.nullable(),
});

export const EventParticipantCreatableSchema =
  _eventParticipantCreatableSchema.readonly();
export type EventParticipantCreatable = z.infer<
  typeof EventParticipantCreatableSchema
>;

export const EventParticipantUpdateableSchema = _eventParticipantCreatableSchema
  .partial()
  .readonly();
export type EventParticipantUpdateable = z.infer<
  typeof EventParticipantUpdateableSchema
>;

const _eventParticipantSchema = _eventParticipantCreatableSchema.extend({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  idPublic: EventParticipantIdPublicSchema,
  eventIdPublic: EventIdPublicSchema,
  userIdPublic: UserIdPublicSchema.nullable(),
  status: EventParticipantStatusSchema,
  statusHistory: z
    .array(statusHistoryEntrySchema(EventParticipantStatusSchema))
    .readonly(),
  profile: eventParticipantProfileSchema.nullable(),
  invalidFields: z.record(z.string(), z.string()),
  isolated: z.boolean(),
});

export const EventParticipantSchema = _eventParticipantSchema.readonly();
export type EventParticipant = z.infer<typeof EventParticipantSchema>;

/**
 * Type alias for event participant status history entry.
 *
 * History entries track all status transitions for full audit trail.
 * The metadata field (JsonObjectType | null) can contain status-specific context:
 * - PENDING: null or { source: 'import' | 'manual' | 'open_invitation' }
 * - INVITED: null or { channel: 'email' | 'whatsapp' }
 * - CONTACTED: null or { trigger: 'whatsapp_message' | 'link_click' }
 * - JOINED: null or { method: 'profile_accepted' }
 * - REMOVED: { reason?: string } (optional removal reason)
 */
export type EventParticipantStatusHistoryEntryType =
  StatusHistoryEntry<EventParticipantStatusType>;

/**
 * Outcome of removing a participant from an event.
 *
 * `already_removed` distinguishes a no-op from a state transition, so callers
 * can stay idempotent without re-reading the participant.
 */
export const RemoveParticipantResultSchema = z
  .object({
    action: z.enum(["removed", "already_removed"]),
  })
  .readonly();

export type RemoveParticipantResult = z.infer<
  typeof RemoveParticipantResultSchema
>;

/**
 * Outcome of seeding demo participants into an event.
 *
 * Counts are reported independently because seeding is not atomic: participants
 * are created first, then enrichment records, then pick step-results, and a
 * later stage failing leaves the earlier counts already persisted.
 */
export const SeedDemoParticipantsResultSchema = z
  .object({
    participantsAdded: z.number(),
    enrichmentRecordsCreated: z.number(),
    pickStepResultsCreated: z.number(),
  })
  .readonly();

export type SeedDemoParticipantsResult = z.infer<
  typeof SeedDemoParticipantsResultSchema
>;

/**
 * Outcome of importing participants into an event from an external source.
 *
 * Counts are reported per action rather than as a processed total because one
 * import reconciles the whole roster: an entry can be added, updated, deleted,
 * restored or skipped in the same pass, and a caller refreshing its own view
 * needs to know which happened.
 */
export const ImportParticipantsResultSchema = z
  .object({
    addedCount: z.number(),
    updatedCount: z.number(),
    deletedCount: z.number(),
    restoredCount: z.number(),
    skippedCount: z.number(),
  })
  .readonly();

export type ImportParticipantsResult = z.infer<
  typeof ImportParticipantsResultSchema
>;

const _invalidFieldsSchema = z.record(z.string(), z.string());

const _addClassificationSchema = z.object({
  type: z.literal("add"),
  invalidFields: _invalidFieldsSchema,
});
const _updateClassificationSchema = z.object({
  type: z.literal("update"),
  invalidFields: _invalidFieldsSchema,
});
const _deleteClassificationSchema = z.object({
  type: z.literal("delete"),
});
const _restoreClassificationSchema = z.object({
  type: z.literal("restore"),
  invalidFields: _invalidFieldsSchema,
});
const _skipClassificationSchema = z.object({
  type: z.literal("skip"),
  reason: z.enum(["excluded_external_id", "removal_status"]),
  invalidFields: _invalidFieldsSchema,
});

/**
 * What an import pass decided to do with one entry, before anything is applied.
 *
 * The arms carry different fields deliberately: `delete` targets an existing
 * participant rather than an incoming row, so it has no fields to validate.
 */
export const ClassificationActionSchema = z.discriminatedUnion("type", [
  _addClassificationSchema,
  _updateClassificationSchema,
  _deleteClassificationSchema,
  _restoreClassificationSchema,
  _skipClassificationSchema,
]);

export type ClassificationAction = z.infer<typeof ClassificationActionSchema>;

/**
 * Outcome of classifying an import payload against the existing roster without
 * applying it — the dry-run counterpart to ImportParticipantsResult.
 */
export const ClassifyParticipantsResultSchema = z
  .object({
    classifications: z.array(ClassificationActionSchema),
    deleteCount: z.number(),
  })
  .readonly();

export type ClassifyParticipantsResult = z.infer<
  typeof ClassifyParticipantsResultSchema
>;
